import { Link } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useState, useEffect } from "react";
import { useLang } from "@/i18n/LanguageContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_rugged-convertion/artifacts/7zmfhnu6_ALEGER%20Logo%20Transparent.png";

function LangToggleImpl({ inline, lang, setLang }) {
  return (
    <div className={`inline-flex items-center gap-1 text-xs font-mono-tech ${inline ? "" : "border border-current/20 px-1"}`} data-testid="lang-switch">
      <Globe size={12} className="mr-1 opacity-60" />
      <button
        onClick={() => setLang("de")}
        data-testid="lang-de"
        className={`px-2 py-1 transition-colors ${lang === "de" ? "text-blue-500 font-bold" : "opacity-60 hover:opacity-100"}`}
      >
        DE
      </button>
      <span className="opacity-30">|</span>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en"
        className={`px-2 py-1 transition-colors ${lang === "en" ? "text-blue-500 font-bold" : "opacity-60 hover:opacity-100"}`}
      >
        EN
      </button>
    </div>
  );
}

export default function Navbar({ dark = false }) {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const base = dark
    ? "bg-[#0B1121]/90 text-white border-b border-white/10 backdrop-blur"
    : scrolled
      ? "bg-white/95 text-slate-900 border-b border-slate-200 backdrop-blur-xl"
      : "bg-transparent text-slate-900 border-b border-transparent";

  const links = [
    { href: "/#problem", label: t.nav.problem, key: "problem" },
    { href: "/#solution", label: t.nav.solution, key: "solution" },
    { href: "/#platform", label: t.nav.platform, key: "platform" },
    { href: "/#use-cases", label: t.nav.useCases, key: "usecases" },
    { href: "/#industries", label: t.nav.industries, key: "industries" },
    { href: "/#pilot", label: t.nav.pilot, key: "pilot" },
    { href: "/#faq", label: t.nav.faq, key: "faq" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-200 ${base}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="ALEGER GLOBAL — Startseite">
          <img
            src={LOGO_URL}
            alt="ALEGER GLOBAL"
            className="h-9 w-auto"
            style={dark ? { filter: "brightness(0) invert(1)" } : {}}
            width="120"
            height="36"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm font-medium">
          {links.map((l) => (
            <a key={l.key} href={l.href} className="hover:text-blue-500 transition-colors" data-testid={`nav-${l.key}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggleImpl lang={lang} setLang={setLang} />
          <a
            href="/#contact"
            data-testid="nav-cta-quote"
            className="hidden sm:inline-flex items-center bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
          >
            {t.nav.ctaPrimary}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Navigation schließen" : "Navigation öffnen"}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white text-slate-900">
          <div className="flex flex-col px-6 py-4 gap-4 text-sm">
            {links.map((l) => (
              <a key={l.key} href={l.href} onClick={() => setOpen(false)}>{l.label}</a>
            ))}
            <a href="/#contact" onClick={() => setOpen(false)} className="bg-blue-600 text-white px-4 py-2 text-center">
              {t.nav.ctaPrimary}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
